import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMemo } from "react";

import { DataTable } from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import type { TradePolicyMetric, TradePolicySimulateResponse } from "../../../types/tradePolicy";
import {
  METRIC_KEYS,
  formatMetricAbsoluteDelta,
  formatMetricPercentChangeFromBaseline,
  formatMetricValue,
  formatSignedPercentChange,
  metricDeltaNumber,
  resolveComparisonLaneColumns,
  resolveFinalBookVsBaselineHeader,
  resolveSimulatedBookColumnHeader,
  signedPnlMetricTone,
} from "./metricFormatters";
import {
  simulatorMetricsDeltaProps,
  simulatorMetricsDeltaToneClass,
  simulatorMetricsHeaderClass,
  simulatorMetricsValueClass,
} from "./simulatorMetricsTypography";

const CELL_PAD = "block w-full px-3";

function MetricsSignedValue({
  metricKey,
  value,
  loading = false,
}: {
  metricKey: string;
  value: unknown;
  loading?: boolean;
}): React.ReactElement {
  const tone = loading ? null : signedPnlMetricTone(metricKey, value);
  return (
    <span
      {...simulatorMetricsDeltaProps(tone)}
      className={cn(simulatorMetricsValueClass, simulatorMetricsDeltaToneClass(tone))}
    >
      {loading ? "…" : formatMetricValue(metricKey, value)}
    </span>
  );
}

function MetricsHeader({
  title,
  align,
  wrap = false,
}: {
  title: string;
  align: "left" | "right";
  wrap?: boolean;
}): React.ReactElement {
  return (
    <span
      className={cn(
        simulatorMetricsHeaderClass,
        CELL_PAD,
        align === "right" ? "text-right" : "text-left",
        wrap && "whitespace-normal leading-snug",
      )}
    >
      {title}
    </span>
  );
}

function MetricsCell({
  align,
  className,
  children,
}: {
  align: "left" | "right";
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn(CELL_PAD, align === "right" ? "text-right" : "text-left", className)}>{children}</div>
  );
}

type MetricRow = {
  key: string;
  label: string;
};

type SimulatorMetricsTableProps = {
  baseline: TradePolicyMetric | undefined;
  filtered: TradePolicyMetric | undefined;
  /** Full simulate result — drives Changed column via `meta.comparison_lane`. */
  result?: Pick<
    TradePolicySimulateResponse,
    "meta" | "removed" | "changed" | "baseline" | "filtered"
  > | null;
  filteredLoading?: boolean;
};

export function SimulatorMetricsTable({
  baseline,
  filtered,
  result,
  filteredLoading = false,
}: SimulatorMetricsTableProps): React.ReactElement {
  const rows = useMemo((): MetricRow[] => METRIC_KEYS.map(({ key, label }) => ({ key, label })), []);
  const laneColumns = useMemo(
    () => resolveComparisonLaneColumns(result ?? { baseline, filtered }),
    [baseline, filtered, result]
  );
  const simulatedBookHeader = useMemo(
    () => resolveSimulatedBookColumnHeader(result ?? { baseline, filtered }),
    [baseline, filtered, result]
  );
  const finalBookVsBaselineHeader = useMemo(
    () => resolveFinalBookVsBaselineHeader(result ?? { baseline, filtered }),
    [baseline, filtered, result]
  );

  const columns = useMemo((): ColumnDef<MetricRow>[] => {
    const cohortColumns: ColumnDef<MetricRow>[] = laneColumns.map((laneColumn) => ({
      id: `comparison-lane-${laneColumn.lane}`,
      header: () => <MetricsHeader title={laneColumn.header} align="right" />,
      cell: ({ row }) => {
        const key = row.original.key;
        const laneVal = laneColumn.metrics?.[key as keyof TradePolicyMetric];
        return (
          <MetricsCell align="right">
            <MetricsSignedValue metricKey={key} value={laneVal} loading={filteredLoading} />
          </MetricsCell>
        );
      },
      enableSorting: false,
    }));

    return [
      {
        accessorKey: "label",
        header: () => <MetricsHeader title="Metric" align="left" />,
        cell: ({ row }) => (
          <MetricsCell align="left">
            <span className={simulatorMetricsValueClass}>{row.original.label}</span>
          </MetricsCell>
        ),
        enableSorting: false,
      },
      {
        id: "baseline",
        header: () => <MetricsHeader title="Baseline" align="right" />,
        cell: ({ row }) => {
          const key = row.original.key;
          const baselineVal = baseline?.[key as keyof TradePolicyMetric];
          return (
            <MetricsCell align="right">
              <MetricsSignedValue metricKey={key} value={baselineVal} />
            </MetricsCell>
          );
        },
        enableSorting: false,
      },
      {
        id: "filtered",
        header: () => <MetricsHeader title={simulatedBookHeader} align="right" />,
        cell: ({ row }) => {
          const key = row.original.key;
          const filteredVal = filtered?.[key as keyof TradePolicyMetric];
          return (
            <MetricsCell align="right">
              <MetricsSignedValue metricKey={key} value={filteredVal} loading={filteredLoading} />
            </MetricsCell>
          );
        },
        enableSorting: false,
      },
      ...cohortColumns,
      {
        id: "change",
        meta: { simulatorMetricsDeltaCol: true },
        header: () => <MetricsHeader title={finalBookVsBaselineHeader} align="right" wrap />,
        cell: ({ row }) => {
          const key = row.original.key;
          const baselineVal = baseline?.[key as keyof TradePolicyMetric];
          const filteredVal = filtered?.[key as keyof TradePolicyMetric];
          const absoluteDelta = filteredLoading
            ? null
            : formatMetricAbsoluteDelta(key, baselineVal, filteredVal);
          const percentChange = filteredLoading
            ? null
            : formatMetricPercentChangeFromBaseline(baselineVal, filteredVal);
          const toneSource = filteredLoading
            ? null
            : metricDeltaNumber(baselineVal, filteredVal) ?? percentChange;
          if (filteredLoading || (absoluteDelta == null && percentChange == null)) {
            return (
              <MetricsCell align="right">
                <span className={simulatorMetricsValueClass}>—</span>
              </MetricsCell>
            );
          }
          return (
            <MetricsCell align="right">
              <span className={cn(simulatorMetricsValueClass, "whitespace-nowrap")}>
                {absoluteDelta != null ? (
                  <span
                    {...simulatorMetricsDeltaProps(toneSource)}
                    className={simulatorMetricsDeltaToneClass(toneSource)}
                  >
                    {absoluteDelta}
                  </span>
                ) : null}
                {percentChange != null ? (
                  <span className="opacity-50">
                    {absoluteDelta != null ? " " : ""}
                    ({formatSignedPercentChange(percentChange)})
                  </span>
                ) : null}
              </span>
            </MetricsCell>
          );
        },
        enableSorting: false,
      },
    ];
  }, [baseline, filtered, filteredLoading, finalBookVsBaselineHeader, laneColumns, simulatedBookHeader]);

  return (
    <div data-simulator-metrics-table="" className="w-full min-w-0">
      <DataTable columns={columns} data={rows} getRowId={(row) => row.key} showPagination={false} />
    </div>
  );
}
